import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  catchError,
  exhaustMap,
  filter,
  fromEvent,
  map,
  merge,
  of,
  tap,
  timer,
} from 'rxjs';
import { environment } from '../../environments/environment';

export interface AppVersion {
  version: string;
  buildTime?: string;
}

export const APP_BUILD_VERSION = new InjectionToken<AppVersion>('APP_BUILD_VERSION', {
  providedIn: 'root',
  factory: () => ({
    version: environment.webCommit,
    buildTime: environment.webCommitTime,
  }),
});

const VERSION_CHECK_INTERVAL_MS = 4 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class DeploymentUpdateService implements OnDestroy {
  private readonly http: HttpClient;
  private readonly updateAvailableSubject = new BehaviorSubject<boolean>(false);
  private checkSubscription?: Subscription;
  private started = false;

  readonly updateAvailable$ = this.updateAvailableSubject.asObservable();

  constructor(
    httpBackend: HttpBackend,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(APP_BUILD_VERSION) private readonly currentBuild: AppVersion,
  ) {
    // Version checks should not show the global loading indicator, attach auth
    // headers, or surface transient failures through the HTTP interceptors.
    this.http = new HttpClient(httpBackend);
  }

  start(): void {
    if (this.started || !this.isUsableVersion(this.currentBuild.version)) {
      return;
    }

    this.started = true;
    const window = this.document.defaultView;
    const focusChecks = window ? fromEvent(window, 'focus') : EMPTY;
    const visibilityChecks = fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
    );

    this.checkSubscription = merge(
      timer(0, VERSION_CHECK_INTERVAL_MS),
      focusChecks,
      visibilityChecks,
    )
      .pipe(
        filter(() => !this.updateAvailableSubject.value),
        exhaustMap(() => this.checkForUpdate()),
      )
      .subscribe();
  }

  checkForUpdate(): Observable<boolean> {
    if (
      this.updateAvailableSubject.value ||
      !this.isUsableVersion(this.currentBuild.version)
    ) {
      return of(this.updateAvailableSubject.value);
    }

    return this.http
      .get<AppVersion>('/version.json', {
        params: { t: Date.now().toString() },
      })
      .pipe(
        map((deployedBuild) => this.isNewerBuild(deployedBuild)),
        tap((updateAvailable) => {
          // This is deliberately sticky during a rolling deployment. Once any
          // new pod is observed, an older pod cannot hide the notification.
          if (updateAvailable) {
            this.updateAvailableSubject.next(true);
          }
        }),
        catchError(() => of(false)),
      );
  }

  reload(): void {
    // reload() preserves the current path/query string and invokes existing
    // beforeunload guards for unsaved editor state.
    this.document.defaultView?.location.reload();
  }

  ngOnDestroy(): void {
    this.checkSubscription?.unsubscribe();
  }

  private isNewerBuild(deployedBuild: AppVersion): boolean {
    if (
      !this.isUsableVersion(deployedBuild?.version) ||
      deployedBuild.version === this.currentBuild.version
    ) {
      return false;
    }

    const currentBuildTime = Date.parse(this.currentBuild.buildTime ?? '');
    const deployedBuildTime = Date.parse(deployedBuild.buildTime ?? '');

    if (!Number.isNaN(currentBuildTime) && !Number.isNaN(deployedBuildTime)) {
      return deployedBuildTime > currentBuildTime;
    }

    // A differing version remains useful for pipelines that provide a build ID
    // without a parseable timestamp.
    return true;
  }

  private isUsableVersion(version: string | undefined): version is string {
    const normalizedVersion = version?.trim();
    return Boolean(normalizedVersion && normalizedVersion !== 'unknown');
  }
}
